// Pure functions that turn the raw WCL CombatantInfo event into normalized
// shapes the UI can render. Lives in shared/ so both the frontend and any
// future Workers code can use it.

import {
  EQUIPMENT_SLOT_NAMES,
  type Aura,
  type EquippedItem,
  type ParsedPlayerInfo,
  type PlayerStats,
  type Spec,
  type TalentTreePoints,
} from './types';
import { inferTalentBuild } from './talents';

interface RawGearEntry {
  id?: number;
  quality?: number;
  icon?: string;
  itemLevel?: number;
  permanentEnchant?: number;
  temporaryEnchant?: number;
  setID?: number;
}

interface RawAuraEntry {
  source?: number;
  ability?: number;
  stacks?: number;
  icon?: string;
  name?: string;
}

interface RawTalentEntry {
  id?: number;
  icon?: string;
}

interface RawCombatantInfo {
  gear?: RawGearEntry[];
  auras?: RawAuraEntry[];
  // talents[i].id is the *total points spent in tree i* (Arms / Fury / Prot).
  talents?: RawTalentEntry[];
  strength?: number;
  agility?: number;
  stamina?: number;
  intellect?: number;
  spirit?: number;
  armor?: number;
}

function parseGear(rawGear: RawGearEntry[] | undefined): EquippedItem[] {
  if (!Array.isArray(rawGear)) return [];
  return rawGear.map((g, idx) => {
    const slot = EQUIPMENT_SLOT_NAMES[idx] ?? `Slot ${idx}`;
    const item: EquippedItem = {
      slot,
      slotIndex: idx,
      itemId: g.id ?? 0,
      quality: g.quality ?? 0,
      itemLevel: g.itemLevel ?? 0,
      icon: g.icon ?? '',
    };
    if (g.permanentEnchant) item.permanentEnchant = g.permanentEnchant;
    if (g.temporaryEnchant) item.temporaryEnchant = g.temporaryEnchant;
    if (g.setID) item.setID = g.setID;
    return item;
  });
}

function parseAuras(rawAuras: RawAuraEntry[] | undefined): Aura[] {
  if (!Array.isArray(rawAuras)) return [];
  return rawAuras
    .filter((a) => typeof a.ability === 'number')
    .map<Aura>((a) => ({
      abilityId: a.ability!,
      name: a.name ?? `spell ${a.ability}`,
      icon: a.icon ?? '',
      source: a.source ?? 0,
      stacks: a.stacks ?? 1,
      origin: 'starting',
    }));
}

// WCL table(dataType: Buffs) return shape. The query nests its real payload
// under "data" — confirmed against vanilla.warcraftlogs.com responses.
interface BuffsTableEntry {
  guid?: number;          // spell id
  name?: string;
  abilityIcon?: string;   // WCL field; CombatantInfo uses 'icon' instead
  icon?: string;
  totalUptime?: number;
  totalUses?: number;
  type?: number;
}

interface BuffsTableInner {
  auras?: BuffsTableEntry[];
  totalTime?: number;
}

interface BuffsTableShape {
  data?: BuffsTableInner;
  // older response format defensiveness
  auras?: BuffsTableEntry[];
  totalTime?: number;
}

const PERSISTENT_THRESHOLD = 0.7;

function mergeBuffTable(starting: Aura[], buffsTable: unknown, fightDurationMs: number): Aura[] {
  if (!buffsTable || typeof buffsTable !== 'object') return starting;
  const outer = buffsTable as BuffsTableShape;
  const inner: BuffsTableInner = outer.data ?? { auras: outer.auras, totalTime: outer.totalTime };
  const tableAuras = Array.isArray(inner.auras) ? inner.auras : [];
  const totalTime = typeof inner.totalTime === 'number' && inner.totalTime > 0
    ? inner.totalTime
    : fightDurationMs;

  const startingIds = new Set(starting.map((a) => a.abilityId));
  const merged: Aura[] = starting.map((a) => ({ ...a }));

  for (const entry of tableAuras) {
    if (typeof entry.guid !== 'number') continue;
    const uptime = entry.totalUptime ?? 0;
    const ratio = totalTime > 0 ? uptime / totalTime : 0;
    const origin = ratio >= PERSISTENT_THRESHOLD ? 'persistent' : 'transient';

    if (startingIds.has(entry.guid)) {
      const existing = merged.find((m) => m.abilityId === entry.guid);
      if (existing) {
        existing.uptimeMs = uptime;
        existing.uses = entry.totalUses ?? 0;
        // Keep origin = 'starting' but enrich with uptime stats.
      }
      continue;
    }
    merged.push({
      abilityId: entry.guid,
      name: entry.name ?? `spell ${entry.guid}`,
      icon: entry.abilityIcon ?? entry.icon ?? '',
      source: 0,
      stacks: 1,
      origin,
      uptimeMs: uptime,
      uses: entry.totalUses ?? 0,
    });
  }

  // Sort: assumed first, then starting, then persistent, then transient.
  const rank: Record<NonNullable<Aura['origin']>, number> = {
    assumed: 0, starting: 1, persistent: 2, transient: 3,
  };
  merged.sort((a, b) => {
    const ra = rank[a.origin ?? 'transient'];
    const rb = rank[b.origin ?? 'transient'];
    if (ra !== rb) return ra - rb;
    const ua = a.uptimeMs ?? 0;
    const ub = b.uptimeMs ?? 0;
    return ub - ua;
  });
  return merged;
}

function parseStats(raw: RawCombatantInfo): PlayerStats {
  return {
    strength: raw.strength ?? 0,
    agility: raw.agility ?? 0,
    stamina: raw.stamina ?? 0,
    intellect: raw.intellect ?? 0,
    spirit: raw.spirit ?? 0,
    armor: raw.armor ?? 0,
  };
}

function parseTreePoints(rawTalents: RawTalentEntry[] | undefined): TalentTreePoints {
  // WCL vanilla schema: talents = [{id: armsPoints}, {id: furyPoints}, {id: protPoints}].
  // Older or partial logs may miss entries — default to 0.
  const arr = Array.isArray(rawTalents) ? rawTalents : [];
  return {
    arms: arr[0]?.id ?? 0,
    fury: arr[1]?.id ?? 0,
    protection: arr[2]?.id ?? 0,
  };
}

function inferSpec(t: TalentTreePoints): Spec {
  if (t.fury >= t.arms && t.fury >= t.protection) return 'fury';
  if (t.arms >= t.protection) return 'arms';
  return 'protection';
}

export function parseCombatantInfo(
  events: unknown[],
  buffsTable: unknown = null,
  fightDurationMs = 0,
): ParsedPlayerInfo | null {
  if (!Array.isArray(events) || events.length === 0) return null;
  const raw = events[0] as RawCombatantInfo;
  const treePoints = parseTreePoints(raw.talents);
  const spec = inferSpec(treePoints);
  const startingAuras = parseAuras(raw.auras);
  const auras = mergeBuffTable(startingAuras, buffsTable, fightDurationMs);
  return {
    gear: parseGear(raw.gear),
    auras,
    stats: parseStats(raw),
    treePoints,
    spec,
    inferredBuild: inferTalentBuild(treePoints),
  };
}
